import {
    Grid,
    Card,
    CardContent,
    Typography,
    Chip,
    Box,
    Divider,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { SandboxResult } from "../../../types/SandboxResult";

interface SandboxResultDetailProps {
    result: SandboxResult;
}

export default function SandboxResultDetail(props: SandboxResultDetailProps) {
    const { result } = props;

    const statusColor = (isSuccessful: boolean) =>
        isSuccessful ? "success" : "error";

    const totalPhases = result.phases.length;
    const successfulPhases = result.phases.filter(p => p.isSuccessful).length;

    const allSteps = result.phases.flatMap(p => p.steps);
    const totalSteps = allSteps.length;
    const successfulSteps = allSteps.filter(s => s.isSuccessful).length;

    const executedAt =
        (result as any).executedAt
            ? new Date((result as any).executedAt)
            : null;

    return (
        <Grid container spacing={1} sx={{ pt: 1 }}>
            {/* Overall summary */}
            <Grid item xs={12}>
                <Card>
                    <CardContent>
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            flexWrap="wrap"
                            rowGap={1}
                        >
                            <Box>
                                <Typography variant="h6">
                                    Test Result
                                </Typography>
                                {executedAt && (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {executedAt.toLocaleString()}
                                    </Typography>
                                )}
                            </Box>

                            <Chip
                                label={result.isSuccessful ? "Successful" : "Failed"}
                                color={statusColor(result.isSuccessful)}
                                sx={{ fontWeight: 600 }}
                            />
                        </Box>

                        <Box
                            display="flex"
                            flexWrap="wrap"
                            columnGap={3}
                            rowGap={0.5}
                            mt={2}
                        >
                            <Typography variant="body2">
                                <strong>Phases:</strong> {successfulPhases} / {totalPhases} OK
                            </Typography>
                            <Typography variant="body2">
                                <strong>Steps:</strong> {successfulSteps} / {totalSteps} OK
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            </Grid>

            {/* Phases as accordions */}
            <Grid item xs={12}>
                <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ mb: 0.5, mt: 0.5 }}
                >
                    Phases
                </Typography>

                {result.phases.map(phase => {
                    const okSteps = phase.steps.filter(s => s.isSuccessful).length;
                    const stepsTotal = phase.steps.length;

                    return (
                        <Accordion
                            key={phase.name}
                            disableGutters
                            sx={{
                                mb: 0.75,
                                borderRadius: 1,
                                "&:before": { display: "none" }
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{
                                    px: 1.5,
                                    "& .MuiAccordionSummary-content": {
                                        alignItems: "center",
                                        gap: 1
                                    }
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        flexGrow: 1,
                                        minWidth: 0
                                    }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        fontWeight={600}
                                        noWrap
                                        title={phase.name}
                                    >
                                        {phase.name}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        Steps: {okSteps} / {stepsTotal} OK
                                    </Typography>
                                </Box>

                                <Chip
                                    label={phase.isSuccessful ? "OK" : "Failed"}
                                    color={statusColor(phase.isSuccessful)}
                                    size="small"
                                />
                            </AccordionSummary>

                            <AccordionDetails sx={{ pt: 0 }}>
                                {/* Phase error, if any */}
                                {!phase.isSuccessful && phase.errorMessage && (
                                    <Typography
                                        variant="body2"
                                        color="error"
                                        sx={{ mb: 1 }}
                                    >
                                        {phase.errorMessage}
                                    </Typography>
                                )}

                                {/* Steps list */}
                                {phase.steps.length > 0 && (
                                    <Box sx={{ mt: 0.5 }}>
                                        <Divider sx={{ mb: 1 }} />
                                        {phase.steps.map(step => (
                                            <Box
                                                key={step.key}
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    mb: 1
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "space-between"
                                                    }}
                                                >
                                                    <Typography variant="body2">
                                                        • {step.key}
                                                    </Typography>

                                                    <Chip
                                                        label={step.isSuccessful ? "OK" : "Failed"}
                                                        color={statusColor(step.isSuccessful)}
                                                        size="small"
                                                    />
                                                </Box>

                                                {!step.isSuccessful && step.errorMessage && (
                                                    <Typography
                                                        variant="body2"
                                                        color="error"
                                                        sx={{ ml: 2, mt: 0.25 }}
                                                    >
                                                        {step.errorMessage}
                                                    </Typography>
                                                )}
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </AccordionDetails>
                        </Accordion>
                    );
                })}
            </Grid>
        </Grid>
    );
}
